import { memo, useEffect, useMemo, useState } from 'react';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors';
import appScreenSelectors from '@/screens/apps/redux/selectors/appScreenSelectors';
import getDateTimeAgo from '@/lib/getDateTimeAgo';
import sendToast from '@/lib/sendToast';
import { dismissAppFixDefect, getAppFixProposals, startAppFixDefect } from '@/redux/actions/gravityActions';
import { FiChevronDown, FiChevronRight, FiPlay, FiRotateCw, FiX } from '@/components/react-icons/main';
import { formatAppFixCost, getCategoryLabel, getSourceLabel, getStatusLabel, statusVariants } from '@/screens/apps/pages/appFixer/constants.jsx';
import DefectConversation, { DefectConversationSkeleton, buildDefectConversation } from '@/screens/apps/pages/appFixer/DefectConversation.jsx';
import Skeleton from './Skeleton.jsx';
import Button from './Button';
import Tag from './Tag';
import IconV2 from './v2/IconV2';

const cardStyle = { border: '1px solid #e5e7eb', borderRadius: 8, backgroundColor: '#fff', overflow: 'hidden' };
const headerStyle = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', cursor: 'pointer' };
const titleStyle = { fontWeight: 500, fontSize: 13, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 };
const tagsStyle = { display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto', flexShrink: 0 };
const metaRowStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '0 12px 10px' };
const metaTextStyle = { fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const activityStyle = { borderTop: '1px solid #f0f0f0', padding: 12 };

// One reported defect, styled to match the App Fixer admin queue: the action it
// affects, the category and status, a source/count/time meta line, and the same
// AI-fix and dismiss actions. Expanding shows the activity conversation (the
// report, the agent's attempts, reviewer feedback) the admin page shows in its
// details modal. Memoized so it does not re-render while the drawer works.
const AppEditorDefectListRow = ({ defect, onChanged }) => {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [proposals, setProposals] = useState(null);

  const actionName = useArgSelector(appScreenSelectors.activeAppActionById, defect?.actionId)?.name;
  const title = actionName || 'App-level defect';
  const status = defect?.status;
  const startable = status === 'open' || status === 'blocked';
  const isRetry = status === 'blocked';
  const dismissable = status && status !== 'dismissed' && status !== 'resolved';
  const cost = formatAppFixCost(defect?.fixJob?.usage?.costUsd);

  // Load the proposal history the first time the row opens, so the activity
  // conversation can show the agent's summaries and any reviewer feedback.
  useEffect(() => {
    if (!expanded || proposals !== null) {
      return;
    }
    getAppFixProposals({ defectId: defect.id }, (data) => setProposals(Array.isArray(data?.proposals) ? data.proposals : []));
  }, [expanded, proposals, defect.id]);

  const conversation = useMemo(() => buildDefectConversation(defect, proposals || []), [defect, proposals]);

  const handleStart = (event) => {
    event.stopPropagation();
    setBusy(true);
    startAppFixDefect({ defectId: defect.id }, (data, err) => {
      setBusy(false);
      if (err) {
        sendToast({ message: err?.message || 'Could not start the AI fix.', type: 'error', duration: 6000 });
        return;
      }
      if (data?.launched === false) {
        sendToast({ message: 'No runner is configured here. Start this fix from the App Fixer admin page.', type: 'info', duration: 6000 });
      } else {
        sendToast({ message: isRetry ? 'AI fix retry started.' : 'AI fix started.', type: 'success', duration: 5000 });
      }
      onChanged?.();
    });
  };

  const handleDismiss = (event) => {
    event.stopPropagation();
    setBusy(true);
    dismissAppFixDefect({ defectId: defect.id }, (data, err) => {
      setBusy(false);
      if (err) {
        sendToast({ message: err?.message || 'Could not dismiss this defect.', type: 'error', duration: 6000 });
        return;
      }
      sendToast({ message: 'Defect dismissed.', type: 'success', duration: 4000 });
      onChanged?.();
    });
  };

  return (
    <div style={cardStyle}>
      <div
        style={headerStyle}
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((value) => !value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setExpanded((value) => !value);
          }
        }}>
        <IconV2 icon={expanded ? FiChevronDown : FiChevronRight} size={14} style={{ color: '#9ca3af', flexShrink: 0 }} />
        <div style={titleStyle} data-title={title}>
          {title}
        </div>
        <div style={tagsStyle}>
          <Tag size="sm">{getCategoryLabel(defect.category)}</Tag>
          <Tag size="sm" variant={statusVariants[status] || 'default'}>
            {getStatusLabel(status)}
          </Tag>
        </div>
      </div>
      <div style={metaRowStyle}>
        <span style={metaTextStyle}>
          {getSourceLabel(defect.source)} · {defect.occurrenceCount || 1}x · {getDateTimeAgo(defect.lastObservedOn) || '—'}
          {cost ? ` · ${cost}` : ''}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
          {dismissable && <Button size="sm" className="ghost" icon={FiX} title="Dismiss" onClick={handleDismiss} disabled={busy} />}
          {startable && <Button size="sm" className="primary" icon={isRetry ? FiRotateCw : FiPlay} title={isRetry ? 'Retry AI fix' : 'Start AI fix'} onClick={handleStart} loading={busy} disabled={busy} />}
        </div>
      </div>
      {expanded && <div style={activityStyle}>{proposals === null ? <DefectConversationSkeleton /> : <DefectConversation events={conversation} />}</div>}
    </div>
  );
};

export default memo(AppEditorDefectListRow);

// One shimmer bar with the shared Skeleton primitive's 8px padding and stacking
// margin neutralized, so it honors the exact size passed in.
const SkeletonBar = ({ width, height = 12, radius = 4 }) => <Skeleton count={1} containerStyle={{ padding: 0, width, flexShrink: 0 }} skeletonStyle={{ width: '100%', height, borderRadius: radius }} />;

// Mirrors one loaded card exactly: it reuses the same cardStyle/headerStyle/
// metaRowStyle, so the header (chevron, title, category and status tags) and the
// meta line (source/count/time and the two action buttons) line up dot for dot
// and the reveal shifts nothing.
const ReportedDefectRowSkeleton = () => (
  <div style={cardStyle}>
    <div style={headerStyle}>
      <SkeletonBar width={14} height={14} />
      <SkeletonBar width={120} height={13} />
      <div style={tagsStyle}>
        <SkeletonBar width={72} height={20} radius={999} />
        <SkeletonBar width={44} height={20} radius={999} />
      </div>
    </div>
    <div style={metaRowStyle}>
      <SkeletonBar width={150} height={12} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
        <SkeletonBar width={26} height={26} radius={6} />
        <SkeletonBar width={26} height={26} radius={6} />
      </div>
    </div>
  </div>
);

// A few placeholder cards while the defect list loads.
export const ReportedDefectsSkeleton = () => (
  <>
    <ReportedDefectRowSkeleton />
    <ReportedDefectRowSkeleton />
    <ReportedDefectRowSkeleton />
  </>
);
